import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Yii } from "libraries/interfaces/Yii";
import { ILabels } from "libraries/interfaces/ILabels";
import { ITextWhithChildrenItem } from "libraries/component/booking-information/components/text-with-children/text-with-children.component";
import { MatTableDataSource } from "@angular/material/table";
import { NotificationType } from "libraries/interfaces/INotification";
import { CardComponent } from "libraries/ui_card/card.component";
import { BookingResourceService } from "libraries/service/resources/booking/bookingResource.service";
import { OperationStatus } from "libraries/service/resources/model/search/IBooking";
import { BookingOperationService } from "libraries/component/booking-information/services/booking-operation.service";
import { IWenji } from "libraries/interfaces/IWenji";
import { LabelService } from "libraries/service/label/label.service";
import { DateService } from "libraries/date/date.service";
import { IBookingEligibility, IBookingOperation, IGetBookingsCommonResponse } from 'libraries/service/resources/booking/interfaces/IGetBookings';
import { IMomentDateFormat } from "libraries/interfaces/IMomentDateFormat";

import { faAngleDown, faAngleUp, IconDefinition } from "@fortawesome/free-solid-svg-icons";

declare const wenji: IWenji;

interface IServiceItem {
    dateTime?: Date;
    serviceTitle: string;
    result?: OperationStatus;
    eligibleLabel: string;
    messages: ITextWhithChildrenItem[];
}

@Component({
    selector: 'booking-services',
    template: `
        <card #cardContainer [isCollapsible]="true">
            <div class="card-header-content">
                <div class="title"  (click)="menuToggleCard()">
                    {{ labels.cardTitle }}
                </div>
            </div>
            <div class="card-body-content">
                <div *ngIf="!dataSource?.data?.length" class="text-cardBodyContent noInformation">
                    {{labels.noService}}
                </div>

                <table mat-table *ngIf="dataSource?.data?.length" [dataSource]="dataSource" multiTemplateDataRows>

                    <ng-container matColumnDef="dateTime">
                        <th mat-header-cell *matHeaderCellDef>
                            {{columnLabels.dateTime}}<br/>
                            <i>{{columnLabels.lastRun}}</i>
                        </th>
                        <td mat-cell *matCellDef="let element">
                            <span class="row-dateTime" *ngIf="element.dateTime" [title]="timezone">
                                {{element.dateTime | dateMoment: dateService.dateFormats.momentMedium}} 
                                - 
                                {{element.dateTime | dateMoment: dateService.dateFormats.momentTime}}
                            </span>
                            <span class="row-dateTime noInformation" *ngIf="!element.dateTime">{{labels.none}}</span>
                            <fa-icon [icon]="element == expandedElement ? iconAngleUp : iconAngleDown" class="icon row-expander"></fa-icon>
                        </td>
                    </ng-container>

                    <ng-container matColumnDef="serviceTitle">
                        <th mat-header-cell *matHeaderCellDef class="text-column"> {{columnLabels.serviceTitle}}</th>
                        <td mat-cell *matCellDef="let element" class="text-column"> {{element.serviceTitle}} </td>
                    </ng-container>

                    <ng-container matColumnDef="result">
                        <th mat-header-cell *matHeaderCellDef class="singleIcon-column">
                            {{columnLabels.result}}
                        </th>
                        <td mat-cell *matCellDef="let element" class="singleIcon-column">
                            <booking-operation-result-icon [result]="element.result"></booking-operation-result-icon>
                        </td>
                    </ng-container>

                    <ng-container matColumnDef="eligible">
                        <th mat-header-cell *matHeaderCellDef class="text-column">
                            {{columnLabels.eligible}}
                        </th>
                        <td mat-cell *matCellDef="let element" class="text-column">
                            {{element.eligibleLabel}}
                        </td>
                    </ng-container>

                    <ng-container matColumnDef="expandedDetail">
                        <td mat-cell *matCellDef="let element" [attr.colspan]="displayedColumns.length">
                            <div class="element-detail" *ngIf="element.messages?.length">
                                <notification [type]="getNotificationType(element.result)">
                                    <text-with-children [values]="element.messages"></text-with-children>
                                </notification>
                            </div>
                        </td>
                    </ng-container>

                    <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>

                    <tr mat-row *matRowDef="let element;  columns: displayedColumns; let i = dataIndex"
                        class="element-row"
                        [class.odd-row]="(i+1) % 2"
                        [class.even-row]="i % 2"
                        [class.expandable-row]="element.messages?.length"
                        [class.collapsed-row]="expandedElement != element"
                        [class.expanded-row]="expandedElement === element"
                        (click)="expandRow(element)">
                    </tr>

                    <tr mat-row *matRowDef="let element; columns: ['expandedDetail']"
                        class="detail-row"
                        [class.collapsed-detail-row]="expandedElement != element"
                        [class.expanded-detail-row]="expandedElement === element"
                    ></tr>

                </table>
            </div>
        </card>
    `,
    styles: [`
        table {
            margin: 0;
        }

        .collapsed-detail-row {
            display: none;
        }

        .expanded-detail-row {
            display: table-row;
        }

        table.mat-table tr.detail-row td {
            padding: 0;
        }

        .element-row td {
            border-bottom-width: 0;
        }

        .expandable-row td {
            cursor: pointer;
        }

        .element-detail {
            overflow: hidden;
            display: flex;
        }

        td .row-expander {
            color: #337ab7;
            margin-left: 8px;
        }

        tr:not(.expandable-row) .row-expander {
            visibility: hidden;
        }

        .mat-column-dateTime {
            width: 225px;
        }

        table.mat-table .singleIcon-column {
            width: 50px;
            text-align: center;
        }

        table.mat-table .text-column {
            text-align: left;
        }

        table tr.mat-header-row th.mat-header-cell,
        table .mat-row .mat-cell {
            padding: .75rem;
            border-color: rgba(189, 195, 199, 0.2);
            border-style: solid;
            border-width: 1px 1px 1px 1px;
            font-size: 1rem;
            border-collapse: collapse;
        }

        table tr.odd-row .mat-cell {
            background-color: #f2f2f2;
        }

        table tr.even-row .mat-cell {
            background-color: #f9fafc;
        }

        table .mat-row .mat-cell,
        table .mat-header-row .mat-header-cell {
            vertical-align: top;
        }

        table .mat-row .mat-cell:first-child,
        table .mat-header-row .mat-header-cell:first-child {
            width: 225px;
            max-width: inherit;
            background-color: #fff;
            text-align: left;
        }

        notification {
            width: 100%;
        }

        .card-body-content {
            max-height: 400px;
            overflow: auto;
        }

        .row-dateTime {
            width: 175px;
            display: inline-block;
        }

        .noInformation {
            color: rgb(110, 110, 110);
        }

        .text-cardBodyContent {
            margin: 12px;
        }

        .card-header-content .title {
            cursor: pointer;
        }
    `]
})
export class BookingServicesComponent implements OnChanges {

    @Input()
    public bookingResult: IGetBookingsCommonResponse;

    @ViewChild('cardContainer')
    public cardContainer: CardComponent;

    @Output()
    public readonly onCardTitleClick: EventEmitter<string> = new EventEmitter();

    public dataSource: MatTableDataSource<IServiceItem>;
    public displayedColumns: string[] = ['dateTime', 'result', 'serviceTitle', 'eligible'];
    public expandedElement: IServiceItem | null;
    public name: string = 'bookingServices';

    public readonly timezone: string = wenji ? wenji.ui.timezone : null;

    public readonly labels: ILabels = {
        cardTitle: Yii.t('wui-shared-js', 'Services Afidium'),
        noService: Yii.t('wui-shared-js', 'Aucun service à afficher.'),
        none: Yii.t('wui-shared-js', 'Aucune exécution'),
    };

    public readonly columnLabels: ILabels = {
        dateTime: Yii.t('wui-shared-js', 'Date'),
        serviceTitle: Yii.t('wui-shared-js', 'Nom'),
        result: Yii.t('wui-shared-js', 'Résultat'),
        lastRun: Yii.t('wui-shared-js', '(dernière exécution)'),
        eligible: Yii.t('wui-shared-js', 'Éligible'),
    };

    public readonly eligibilityLabels: ILabels = {
        notApplicable: Yii.t('wui-shared-js', 'Non éligible'),
        inhibited: Yii.t('wui-shared-js', 'Désactivé'),
        applicable_now: Yii.t('wui-shared-js', 'Éligible'),
        applicable_event_pnrUpdate: Yii.t('wui-shared-js', 'À la prochaine mise à jour du Dossier GDS'),
        applicable_event_bookingUpdate: Yii.t('wui-shared-js', 'À la prochaine mise à jour du Dossier Mid-Office'),
        applicable_dateRange_start: Yii.t('wui-shared-js', 'À partir du %start_datetime%'),
        applicable_dateRange_end: Yii.t('wui-shared-js', 'Jusqu\'au %end_datetime%'),
    };

    public iconAngleDown: IconDefinition = faAngleDown;
    public iconAngleUp: IconDefinition = faAngleUp;

    constructor(public readonly dateService: DateService,
        private readonly bookingResourceService: BookingResourceService,
        private readonly bookingOperationService: BookingOperationService,
        private readonly labelService: LabelService, ) {
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const curBookingResult: IGetBookingsCommonResponse = changes.bookingResult.currentValue;
        if (!curBookingResult) {
            return;
        }
        this.doInit(curBookingResult);
    }

    public doInit(curBookingResult: IGetBookingsCommonResponse): void {

        const services: { [serviceCode: string]: IServiceItem } = {};

        (curBookingResult.operations || []).forEach((curOper: IBookingOperation): void => {
            const prevOper: IServiceItem = services[curOper.service.code];

            const curOperDateTme: Date = new Date(curOper.timestamp);
            if (prevOper && curOperDateTme < prevOper.dateTime) {
                return;
            }

            services[curOper.service.code] = {
                serviceTitle: curOper.service.title,
                dateTime: curOperDateTme,
                result: this.bookingOperationService.getOperationsStatus(curOper.operations),
                eligibleLabel: null,
                messages: this.bookingOperationService.getOperationItems(curOper.operations)
            };
        });

        (curBookingResult.eligibility || []).forEach((curElig: IBookingEligibility) => {
            const service: IServiceItem = services[curElig.code];
            if (!service) {
                services[curElig.code] = {
                    dateTime: null, eligibleLabel: this.getEligibleLabel(curElig), messages: [], serviceTitle: curElig.title, result: null
                };
            } else {
                service.eligibleLabel = this.getEligibleLabel(curElig);
            }
        });
        this.dataSource = new MatTableDataSource<IServiceItem>(Object.values(services)
            .sort((a: IServiceItem, b: IServiceItem) => a.serviceTitle.localeCompare(b.serviceTitle)));

    }

    public getEligibleLabel(eligibility: IBookingEligibility): string {
        if (eligibility.status === "NotApplicable") {
            return this.eligibilityLabels.notApplicable;
        } else if (eligibility.status === 'Inhibited') {
            return this.eligibilityLabels.inhibited;
        } else if (eligibility.status === 'Applicable' && eligibility.details.type === 'NOW') {
            return this.eligibilityLabels.applicable_now;
        } else if (eligibility.status === 'Applicable' && eligibility.details.type === 'EVENT' && eligibility.details.event === 'PNR_UPDATE') {
            return this.eligibilityLabels.applicable_event_pnrUpdate;
        } else if (eligibility.status === 'Applicable' && eligibility.details.type === 'EVENT' && eligibility.details.event === 'BOOKING_UPDATE') {
            return this.eligibilityLabels.applicable_event_bookingUpdate;
        } else if (eligibility.status === 'Applicable' && eligibility.details.type === 'DATE_RANGE') {
            const dateRangeLabels: string[] = [];
            if (eligibility.details.start) {
                const startLabel: string = this.labelService.completeLabel(
                    this.eligibilityLabels.applicable_dateRange_start,
                    { start_datetime: this.formatDateTime(eligibility.details.start) });
                dateRangeLabels.push(startLabel);
            }
            if (eligibility.details.end) {
                const endLabel: string = this.labelService.completeLabel(
                    this.eligibilityLabels.applicable_dateRange_end,
                    { end_datetime: this.formatDateTime(eligibility.details.end) });
                dateRangeLabels.push(endLabel);
            }
            return dateRangeLabels.join(' / ');
        } else {
            const detailsType: string = eligibility.details ? eligibility.details.type : null;
            const detailsEvent: string = eligibility.details.event ? eligibility.details.type : null;
            return `${eligibility.status}_${detailsType}_${detailsEvent}`;
        }
    }

    public getNotificationType(result: OperationStatus): NotificationType {
        return this.bookingOperationService.getNotificationType(result);
    }

    public expandRow(element: IServiceItem): void {
        if (element.messages.length) {
            this.expandedElement = this.expandedElement === element ? null : element;
        }
    }

    public menuToggleCard(): void {
        this.onCardTitleClick.emit(this.name);
    }

    private formatDateTime(inputDateStr?: string): string {
        if (!inputDateStr) {
            return inputDateStr;
        }

        let resultDateStr: string = this.dateService.getDateFormatted(inputDateStr, this.dateService.dateFormats.momentShort);
        if (inputDateStr.indexOf('T') !== -1) {
            resultDateStr += ` - ${this.dateService.getDateFormatted(inputDateStr, this.dateService.dateFormats.momentTime)}`;
        }
        return resultDateStr;
    }
}
